import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { KeyRound, Loader2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useLanguage } from '../../context/LanguageContext';

const PendingRow = ({ item, el, onConfirmed }) => {
    const [plate, setPlate] = useState('');
    const [saving, setSaving] = useState(false);

    const confirm = async () => {
        if (!plate.trim()) return;
        setSaving(true);
        try {
            await api.post(`/leasing/pending/${item.id}/confirm`, { license_plate: plate });
            toast.success(el ? 'Το όχημα καταχωρήθηκε επιτυχώς' : 'Vehicle registered successfully');
            onConfirmed();
        } catch (error) {
            const detail = error.response?.data?.detail;
            toast.error(detail === 'License plate does not match'
                ? (el ? 'Η πινακίδα δεν ταιριάζει' : 'License plate does not match')
                : (detail || (el ? 'Αποτυχία επιβεβαίωσης' : 'Confirmation failed')));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex flex-col md:flex-row md:items-center gap-3 p-3 bg-white rounded-sm border border-amber-200" data-testid={`pending-leased-${item.id}`}>
            <div className="flex-1">
                <p className="font-semibold text-slate-800">{item.make} {item.model} {item.year}</p>
                <p className="text-xs text-slate-500">{el ? 'Από' : 'From'}: {item.leasing_company_name}</p>
            </div>
            <Input
                value={plate}
                onChange={(e) => setPlate(e.target.value)}
                placeholder={el ? 'Πληκτρολογήστε την πινακίδα' : 'Enter license plate'}
                className="md:w-56 bg-white border-slate-300"
                data-testid={`pending-leased-plate-${item.id}`}
            />
            <Button onClick={confirm} disabled={saving || !plate.trim()} className="bg-amber-500 hover:bg-amber-600 text-white rounded-sm" data-testid={`pending-leased-confirm-${item.id}`}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><KeyRound className="w-4 h-4 mr-2" />{el ? 'Επιβεβαίωση' : 'Confirm'}</>}
            </Button>
        </div>
    );
};

export const PendingLeasedVehicles = ({ onConfirmed }) => {
    const { language } = useLanguage();
    const el = language === 'el';
    const [items, setItems] = useState([]);

    const load = useCallback(async () => {
        try {
            const res = await api.get('/leasing/pending');
            setItems(res.data);
        } catch {
            setItems([]);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (!items.length) return null;

    return (
        <div className="fleet-card p-4 border-l-4 border-amber-500 bg-amber-50/50 space-y-3" data-testid="pending-leased-vehicles">
            <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-600" />
                <h2 className="text-base md:text-lg font-semibold text-slate-800">
                    {el ? 'Οχήματα Leasing σε αναμονή' : 'Pending leasing vehicles'} ({items.length})
                </h2>
            </div>
            <p className="text-sm text-slate-600">
                {el ? 'Η εταιρεία leasing σας έχει αναθέσει τα παρακάτω οχήματα. Πληκτρολογήστε την πινακίδα για επιβεβαίωση.'
                    : 'Your leasing company assigned these vehicles to you. Enter the license plate to confirm.'}
            </p>
            {items.map((item) => (
                <PendingRow key={item.id} item={item} el={el} onConfirmed={() => { load(); onConfirmed?.(); }} />
            ))}
        </div>
    );
};
